import {
  GraduationCap,
  LayoutDashboard,
  BarChart3,
  Users,
  Calendar,
  FileText,
  HelpCircle,
  User,
  UserCheck,
  BookOpen,
  Target,
  Receipt,
  Wallet,
  CalendarDays, Briefcase, ClipboardList, ClipboardCheck, MessageSquare, Music, Globe, Smartphone } from 'lucide-react';
import type { NavigationConfig, NavigationGroup, NavigationItem } from './types';

// Main-app only supports employee and manager roles
// Admin dashboards are available in the admin-portal
// One dashboard for everyone. Managers don't get a separate "Manager Dashboard"
// — they see the same dashboard plus the Team Management nav group below.
const dashboards: NavigationItem[] = [
  {
    id: 'employee-dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    path: 'dashboard',
    isDashboard: true,
    requiredRoles: ['employee', 'manager'],
    dashboardMetadata: {
      description: 'Personal overview and quick actions',
      defaultFor: ['employee', 'manager'],
      category: 'personal'
    }
  }
];

const navigationGroups: NavigationGroup[] = [
  {
    id: 'main',
    label: 'Main',
    order: 1,
    items: [
      {
        id: 'dashboard-selector',
        label: 'Dashboard',
        icon: LayoutDashboard,
        path: '', // Dynamic based on preference
        isDashboard: true
      }
    ]
  },
  {
    id: 'personal',
    label: 'Personal',
    order: 2,
    requiredRoles: ['employee', 'manager'],
    items: [
      {
        id: 'my-profile',
        label: 'My Profile',
        icon: User,
        path: 'personal',
        requiredRoles: ['employee', 'manager']
      },
      {
        id: 'time-off',
        label: 'Time Off',
        icon: Calendar,
        path: 'time-off',
        requiredRoles: ['employee', 'manager']
      },
      {
        id: 'my-assignments',
        label: 'My Assignments',
        icon: Briefcase,
        path: 'my-assignments',
        requiredRoles: ['employee', 'manager']
      },
      {
        id: 'work',
        label: 'Work',
        icon: ClipboardList,
        path: 'work',
        requiredRoles: ['employee', 'manager'],
        // Universal Work: one tabbed surface (My Work · Created by Me ·
        // Collaborating · Team Work · Reviews · Templates); tabs gate
        // themselves in-page from the tenant policy + permissions. Tenant
        // entitlement switch: nav renders only when the tenant holds the `work`
        // product. Hiding is UX — the route guard + gateway entitlement policy
        // + backend RBAC enforce regardless.
        requiredFeatures: ['work'],
      },
      {
        id: 'messages',
        label: 'Conversations',
        icon: MessageSquare,
        path: 'messages',
        requiredRoles: ['employee', 'manager'],
        // Nav renders only when the tenant holds the `conversations` product.
        // Hiding is UX — the gateway + conversation-service enforce the entitlement
        // (403) regardless.
        requiredFeatures: ['conversations'],
      },
      {
        id: 'web',
        label: 'Web',
        icon: Globe,
        path: 'web',
        requiredRoles: ['employee', 'manager'],
        // KaziPeople Web (Work Browser). Nav renders only when the tenant holds the
        // `work_browser` product (granted in the admin portal). Hiding is UX — the
        // gateway entitlement policy + auth-service RBAC enforce regardless.
        requiredFeatures: ['work_browser'],
      },
      {
        id: 'my-calendar',
        label: 'My Calendar',
        icon: CalendarDays,
        path: 'my-calendar',
        requiredRoles: ['employee', 'manager']
      },
      {
        id: 'documents',
        label: 'Documents',
        icon: FileText,
        path: 'documents',
        requiredRoles: ['employee', 'manager']
      },
      {
        id: 'employee-directory',
        label: 'Employee Directory',
        icon: Users,
        path: 'employees',
        requiredRoles: ['employee', 'manager']
      },
      {
        id: 'my-pay',
        label: 'My Pay',
        icon: Wallet,
        path: 'my-pay',
        requiredRoles: ['employee', 'manager']
      },
      {
        id: 'my-surveys',
        label: 'My Surveys',
        icon: ClipboardCheck,
        path: 'my-surveys',
        requiredRoles: ['employee', 'manager']
      },
      {
        id: 'performance',
        label: 'Performance',
        icon: Target,
        path: 'performance',
        requiredRoles: ['employee', 'manager']
      },
      {
        id: 'music',
        label: 'Music',
        icon: Music,
        path: 'music',
        requiredRoles: ['employee', 'manager'],
        // Tenant entitlement switch: nav renders only when the tenant holds the
        // `music` product (granted in the admin portal). Hiding is UX — the
        // gateway + performance-service enforce the entitlement regardless.
        requiredFeatures: ['music'],
      },
      {
        id: 'expenses',
        label: 'Expenses',
        icon: Wallet,
        path: 'expenses',
        requiredRoles: ['employee', 'manager'],
        // Backend entitlement (AMAS pilot): nav renders only when the tenant
        // holds the `expenses` product. Hiding is UX — the gateway + backend
        // enforce the entitlement and RBAC regardless.
        requiredFeatures: ['expenses'],
      },
      {
        id: 'everyday-services',
        label: 'Everyday Services',
        icon: Smartphone,
        path: 'everyday-services',
        requiredRoles: ['employee', 'manager'],
        // Everyday Services (airtime / data / LUKU), paid from the employee's own
        // mobile money. Nav renders only when the tenant holds the `commerce`
        // product; the employer's on/off switch is enforced by commerce-service
        // (/availability → enabled:false), and the gateway enforces regardless.
        requiredFeatures: ['commerce'],
      },
      {
        id: 'training',
        label: 'Learning & Development',
        icon: GraduationCap,
        path: 'training',
        requiredRoles: ['employee', 'manager'],
        // Operator-toggleable via admin-portal Products & Entitlements
        // (`learning` in the product catalog). UX only — the gateway policy
        // enforces when ENTITLEMENT_ENFORCEMENT is on. Un-provisioned tenants
        // fail open (hasProduct semantics), so nothing breaks pre-migration.
        requiredFeatures: ['learning'],
      }
    ]
  },
  {
    id: 'team',
    label: 'Team Management',
    order: 3,
    requiredRoles: ['manager'],
    requiredPermissions: ['leave:approve'],
    items: [
      {
        id: 'my-team',
        label: 'My Team',
        icon: Users,
        path: 'team',
        requiredRoles: ['manager'],
        requiredPermissions: ['leave:approve']
      },
      {
        id: 'leave-approvals',
        label: 'Leave Approvals',
        icon: UserCheck,
        path: 'leave-approvals',
        requiredRoles: ['manager'],
        requiredPermissions: ['leave:approve'],
        // Someone covering a manager's leave approvals (an active delegation)
        // gets this entry too; leave-service lists only the delegated people.
        delegable: true
      },
      {
        id: 'team-learning',
        label: 'Team Learning',
        icon: GraduationCap,
        path: 'team-learning',
        requiredRoles: ['manager'],
        // Anyone who manages people: the learning:assign permission (role or
        // custom grant) is the gate; the backend narrows every list/write to
        // the caller's reachable set. Entitlement-gated like personal Learning.
        requiredPermissions: ['learning:assign'],
        requiredFeatures: ['learning'],
      },
      {
        id: 'hr-actions',
        label: 'HR Action Center',
        icon: ClipboardList,
        path: 'hr-actions',
        requiredRoles: ['manager'],
        requiredPermissions: ['performance:manage'],
      },
      {
        id: 'surveys',
        label: 'Surveys',
        icon: ClipboardCheck,
        path: 'surveys',
        requiredRoles: ['manager'],
        requiredPermissions: ['performance:manage'],
      },
      {
        id: 'hr-action-settings',
        label: 'HR Action Settings',
        icon: ClipboardList,
        path: 'hr-actions/settings',
        requiredRoles: ['manager'],
        requiredPermissions: ['performance:manage'],
      },
      {
        id: 'team-calendar',
        label: 'Team Calendar',
        icon: CalendarDays,
        path: 'calendar',
        requiredRoles: ['manager'],
        requiredPermissions: ['leave:approve']
      },
      // Performance removed — not built yet (page/route deleted).
      {
        id: 'team-reports',
        label: 'Team Reports',
        icon: BarChart3,
        path: 'team-reports',
        requiredRoles: ['manager'],
        requiredPermissions: ['reports:view']
      }
    ]
  },
  // Payroll operations live in a hardcoded, canonical-RBAC-gated section in
  // TenantAwareNavigationSidebar (like Administration), because the legacy
  // nav-role path maps company owners to 'manager' and wouldn't grant a registry
  // permission gate. See the __payroll__ section there.
  // Organization, System, and Admin Settings are available in the admin-portal
  {
    id: 'support',
    label: 'Support',
    order: 99,
    items: [
      {
        id: 'help',
        label: 'Help & Support',
        icon: HelpCircle,
        path: 'help'
      }
    ]
  }
];

export const navigationConfig: NavigationConfig = {
  groups: navigationGroups,
  dashboards
};